import clsx from 'clsx';
import ContentfulStructuredData from 'components/Shared/ContentfulStructuredData';
import ConversionBar from 'components/Shared/ConversionBar';
import Breadcrumbs from 'components/ui/Breadcrumbs';
import ContentBlocks from 'components/ui/ContentBlocks';
import BlockLoaderNonFields from 'components/ui/ContentBlocks/BlockLoaderNonFields';
import { useLivePreview } from 'hooks/useLivePreview';
import type { GeneralPage as GeneralPageType } from 'types/GeneralPage';
import type { FullWidthContentBlock } from 'types/PageData';

type Props = {
  entry: GeneralPageType;
  fullWidthBlocks: {
    top: FullWidthContentBlock | null;
    bottom: FullWidthContentBlock | null;
  };
  sidebar: boolean;
};

const GeneralPage = ({
  entry,
  fullWidthBlocks,
  sidebar,
}: Props): JSX.Element => {
  const { slug, sys } = entry;
  const isB2bPage = slug === 'b2b' || sys.id === '6IiA4GFUwGLDm7Hfq89E8z';
  const { updatedEntity, inspectorProps } = useLivePreview(entry);

  const breadcrumbs = [
    {
      link: slug,
      title: updatedEntity.title,
    },
  ];

  return (
    <>
      <div>
        {updatedEntity.schemaMarkupJsonLd && (
          <ContentfulStructuredData schema={updatedEntity.schemaMarkupJsonLd} />
        )}
        {fullWidthBlocks?.top && (
          <div className="mb-8">
            <ContentBlocks
              type={fullWidthBlocks.top.__typename as string}
              fields={fullWidthBlocks.top}
              config={{
                useH1: true,
                isLandingPage: slug === 'ukraine',
              }}
            />
          </div>
        )}
        <div className="max-w-screen-xl mx-auto mb-6">
          <div className="md:grid md:grid-cols-6 md:gap-x-5">
            <div
              className={clsx(
                isB2bPage ? 'md:col-span-6' : 'md:col-span-4',
                sidebar ||
                  (fullWidthBlocks && !fullWidthBlocks.top) ||
                  isB2bPage
                  ? ''
                  : 'md:col-start-2',
                isB2bPage && 'md:col-start-1',
              )}
            >
              <Breadcrumbs
                containerClassNames={clsx(
                  'hidden',
                  isB2bPage && 'md:block',
                  fullWidthBlocks?.top && '-mt-8',
                )}
                className="md:pb-8"
                crumbs={breadcrumbs}
              />
              {!updatedEntity.disableH1 && (
                <div className="max-w-screen-xl mx-auto px-5 lg:px-4">
                  <h1
                    className="text-primary text-4_3xl lg:text-5xl hyphenate"
                    {...inspectorProps({ fieldId: 'title' })}
                  >
                    {updatedEntity.title}
                  </h1>
                </div>
              )}
              <div>
                {updatedEntity.blocks && (
                  <BlockLoaderNonFields entry={updatedEntity} field="blocks" />
                )}
              </div>
            </div>
            {sidebar && <div>{sidebar}</div>}
          </div>
        </div>
        {fullWidthBlocks?.bottom && (
          <ContentBlocks
            type={fullWidthBlocks.bottom.__typename as string}
            fields={fullWidthBlocks.bottom}
          />
        )}
      </div>
      {entry.showStickyConversionBar && <ConversionBar />}
    </>
  );
};

export default GeneralPage;
